import { ChangeEventHandler, FocusEventHandler, forwardRef, memo, PropsWithChildren } from 'react';
import { DropDownOption } from 'src/types';
import { useClickOutside } from 'react-easyrocket';
import { twJoin, twMerge } from 'tailwind-merge';
import { OptionsList } from './components/OptionsList';
import { OptionsWrapper } from './components/OptionsWrapper';
import { Option } from './components/Option';
import { AutocompleteProps } from './types';
import { useRefs } from './hooks/useRefs';
import { useFocusBlur } from './hooks/useFocusBlur';
import { useSuggestions } from './hooks/useSuggestions';
import { useFakeChangeEvent } from './hooks/useFakeChangeEvent';
import { useSelfValidation } from './hooks/useSelfValidation';
import { useOuterValueCheck } from './hooks/useOuterValueCheck';
import { Input } from '../Input';
import { AutocompleteIcon } from 'src/components/svg/General';

const _Autocomplete = forwardRef<HTMLInputElement, PropsWithChildren<AutocompleteProps>>(
  function _Autocomplete(
    {
      options = [],
      onChange,
      className,
      emptyStateMessage = 'No such option',
      errorMessage,
      value,
      defaultValue,
      autocompleteClassName,
      selfValidate = false,
      children,
      inputWrapperProps,
      inputFrameProps,
      onFocus,
      ...props
    },
    _ref
  ) {
    const { containerRef, ref, setRefValue } = useRefs(_ref);

    const {
      isFocused,
      blur,
      focus,
      handleArrowIconClick,
      handleInputFrameClick,
      handleInputWrapperClick
    } = useFocusBlur({
      onInputWrapperClick: inputWrapperProps?.onClick,
      onInputFrameClick: inputFrameProps?.onClick
    });
    const { suggestions, setSuggestions, filterOptions } = useSuggestions(options);
    const makeChangeEvent = useFakeChangeEvent(props.name);

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      if (props.disabled) return;
      const value = e.target.value.toLowerCase();
      const filtered = filterOptions(value);
      setSuggestions(filtered);
      const hasExactMatchingOption =
        filtered.length === 1 && filtered[0].name.toLowerCase() === value;
      onChange?.(e, hasExactMatchingOption ? filtered[0] : undefined);
    };

    const handleSelectOption = (option: DropDownOption) => {
      onChange?.(makeChangeEvent(option.name), option);
      setSuggestions([option]);
      setRefValue(option.name);
      blur();
    };

    const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
      e.stopPropagation();
      e.preventDefault();
      focus();
      onFocus?.(e);
    };

    useClickOutside(containerRef, blur);

    useOuterValueCheck({
      isFocused,
      value,
      onChange,
      makeChangeEvent,
      setRefValue,
      defaultValue,
      options,
      filterOptions,
      handleSelectOption
    });

    const { validationError } = useSelfValidation({
      isFocused,
      options,
      ref,
      selfValidate
    });

    return (
      <div
        className={twMerge('relative', autocompleteClassName)}
        ref={containerRef}
        data-role="autocomplete-input">
        <Input
          ref={ref}
          {...props}
          inputWrapperProps={{
            ...inputWrapperProps,
            onClick: handleInputWrapperClick
          }}
          onChange={handleInputChange}
          className={className}
          onFocus={handleFocus}
          rightIcon={
            <AutocompleteIcon
              height="20px"
              className={twJoin(
                isFocused && 'rotate-180',
                'transition-transform',
                props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              )}
              onClick={(e) => {
                if (props.disabled) return;
                handleArrowIconClick(e);
              }}
            />
          }
          errorMessage={isFocused ? undefined : validationError || errorMessage}
          inputFrameProps={{
            ...inputFrameProps,
            className: twMerge('relative z-40', inputFrameProps?.className),
            onClick: handleInputFrameClick
          }}
          value={value}
          defaultValue={defaultValue}
        />
        {isFocused && !props.disabled && (
          <OptionsWrapper>
            {suggestions.length === 0 && <Option name={emptyStateMessage} />}
            <OptionsList
              options={suggestions.length > 1 ? suggestions : options}
              onClick={handleSelectOption}
              priorityElementId={suggestions.length === 1 ? suggestions[0].id : undefined}
            />
          </OptionsWrapper>
        )}
        {children}
      </div>
    );
  }
);

export const Autocomplete = memo(_Autocomplete);
